// Portal del Cliente — vista única con 5 secciones (tabs). El dispatcher
// (routes/pages.tsx) pasa `tab`. Multi-tenant: cualquier org con clientes lo
// reutiliza. Usa tokens de tema (bg-card/text-foreground/…) para respetar
// modo claro y oscuro.

import { useEffect, useState } from 'react';
import {
  Briefcase, Receipt, Clock, FileText, Phone, MessageCircle,
  CalendarClock, CheckCircle2, AlertTriangle, Download, Scale,
} from 'lucide-react';

import { fetchClientPortal } from '../api';
import type { ClientPortalData } from '../types';

const money = (n: number) =>
  n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 });

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`rounded-xl border border-border bg-card p-5 ${className}`}>{children}</div>
  );
}

function SectionTitle({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground mb-4">
      <span className="text-primary">{icon}</span>{children}
    </h2>
  );
}

export function ClientPortalView({ tab = 'home' }: { tab?: string }) {
  const [data, setData] = useState<ClientPortalData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    fetchClientPortal()
      .then((d) => { if (alive) setData(d); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading || !data) {
    return <div className="p-8 text-muted-foreground">Cargando tu portal…</div>;
  }

  const { caso, bono, cuenta, documentos, clienteNombre } = data;
  const pct = bono.contratadas > 0 ? Math.round((bono.saldo / bono.contratadas) * 100) : 0;

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6">
      {/* saludo */}
      <div>
        <p className="text-sm text-muted-foreground">Portal del cliente</p>
        <h1 className="text-2xl font-bold text-foreground">Hola, {clienteNombre.split(' ')[0]}</h1>
        {data.isDemo && (
          <p className="mt-1 text-xs text-muted-foreground">
            Vista de demostración · los datos se conectan al expediente real cuando el despacho lo captura.
          </p>
        )}
      </div>

      {/* ── MI CASO ── */}
      {tab === 'home' && (
        <div className="space-y-4">
          <Card>
            <SectionTitle icon={<Scale size={18} />}>Mi caso</SectionTitle>
            <div className="space-y-1 text-sm">
              <p className="text-foreground font-medium">{caso.expediente}</p>
              <p className="text-muted-foreground">{caso.juzgado}</p>
              <div className="flex flex-wrap gap-2 pt-2">
                <span className="rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-medium">{caso.estatus}</span>
                <span className="rounded-full bg-muted text-muted-foreground px-3 py-1 text-xs">Responsable: {caso.abogado}</span>
              </div>
            </div>
          </Card>
          <Card>
            <SectionTitle icon={<CalendarClock size={18} />}>Avance del expediente</SectionTitle>
            <ul className="space-y-3">
              {caso.eventos.map((e, i) => (
                <li key={i} className="flex gap-3">
                  <div className={`mt-0.5 h-2.5 w-2.5 rounded-full shrink-0 ${e.urgente ? 'bg-primary' : 'bg-border'}`} />
                  <div>
                    <p className="text-sm text-foreground">
                      <span className="font-medium">{e.tipo}</span>
                      {e.urgente && <span className="ml-2 rounded bg-primary/15 text-primary px-1.5 py-0.5 text-[11px] font-semibold">Próximo</span>}
                    </p>
                    <p className="text-xs text-muted-foreground">{e.fecha} · {e.detalle}</p>
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      )}

      {/* ── ESTADO DE CUENTA (cuánto debe) ── */}
      {tab === 'cuenta' && (
        <Card className={cuenta.alCorriente ? '' : 'border-primary/40'}>
          <SectionTitle icon={<Receipt size={18} />}>Estado de cuenta</SectionTitle>
          {cuenta.alCorriente ? (
            <div className="flex items-center gap-2 text-foreground">
              <CheckCircle2 size={18} className="text-green-600 dark:text-green-400" />
              Estás al corriente. ¡Gracias!
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Adeudo pendiente</p>
                <p className="text-3xl font-bold text-foreground tabular-nums">{money(cuenta.adeudo)}</p>
                <p className="text-sm text-muted-foreground">{cuenta.concepto}{cuenta.vencido ? ' · fecha vencida' : ''}</p>
              </div>
              <div className="flex items-start gap-2 rounded-lg bg-muted p-3 text-sm text-muted-foreground">
                <AlertTriangle size={16} className="text-primary mt-0.5 shrink-0" />
                <span>Con un adeudo pendiente algunas gestiones quedan en pausa. Lo urgente de tu caso (como una audiencia próxima) siempre se atiende.</span>
              </div>
              <a
                href={`https://wa.me/${caso.abogadoWhatsapp}?text=${encodeURIComponent('Hola, quiero regularizar mi adeudo con KNP Abogados.')}`}
                target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
              >
                <MessageCircle size={16} /> Regularizar por WhatsApp
              </a>
            </div>
          )}
        </Card>
      )}

      {/* ── BONO DE HORAS ── */}
      {tab === 'bono' && (
        <Card>
          <SectionTitle icon={<Clock size={18} />}>Bono de horas</SectionTitle>
          <p className="text-sm text-muted-foreground">Plan {bono.plan}</p>
          <p className="mt-1 text-3xl font-bold text-foreground tabular-nums">{bono.saldo} h <span className="text-base font-normal text-muted-foreground">de {bono.contratadas} h</span></p>
          <div className="mt-3 h-2 w-full rounded-full bg-muted overflow-hidden">
            <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
          </div>
          <p className="mt-2 text-xs text-muted-foreground">Usadas: {bono.usadas} h · el saldo se descuenta conforme tu abogado registra trabajo.</p>
        </Card>
      )}

      {/* ── DOCUMENTOS ── */}
      {tab === 'documentos' && (
        <Card>
          <SectionTitle icon={<FileText size={18} />}>Documentos compartidos</SectionTitle>
          <ul className="divide-y divide-border">
            {documentos.map((d, i) => (
              <li key={i} className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <FileText size={18} className="text-muted-foreground" />
                  <div>
                    <p className="text-sm text-foreground">{d.nombre}</p>
                    <p className="text-xs text-muted-foreground">{d.tipo} · {d.fecha}</p>
                  </div>
                </div>
                <button className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs text-foreground hover:bg-muted">
                  <Download size={14} /> Descargar
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* ── MI ABOGADO ── */}
      {tab === 'contacto' && (
        <Card>
          <SectionTitle icon={<Briefcase size={18} />}>Mi abogado</SectionTitle>
          <p className="text-lg font-medium text-foreground">{caso.abogado}</p>
          <p className="text-sm text-muted-foreground">Responsable de tu expediente {caso.expediente.split(' ').slice(-1)[0]}</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <a href={`tel:${caso.abogadoTelefono}`} className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90">
              <Phone size={16} /> Llamar
            </a>
            <a href={`https://wa.me/${caso.abogadoWhatsapp}?text=${encodeURIComponent('Hola, necesito hablar con mi abogado sobre mi caso.')}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted">
              <MessageCircle size={16} /> Enviar mensaje
            </a>
          </div>
        </Card>
      )}
    </div>
  );
}
